import config from '../config';

const percentageType = 'percentage';
const flatTypes = new Set(['flat', 'fixed']);

const discountReason = (discount) => discount?.reason || discount?.kind;

export const discountReasonLabel = (discount) => {
    const reason = String(discountReason(discount) || '').trim().toLowerCase();
    if (reason === 'welcome') return 'Descuento de bienvenida';
    if (reason === 'holiday') return 'Descuento de feriado';
    return 'Descuento global';
};

export const sharedDiscountReasonLabel = (discounts = []) => {
    const labels = [...new Set(discounts.map(discountReasonLabel))];
    return labels.length === 1 ? labels[0] : 'Descuento global';
};

export const normalizeActiveGlobalDiscount = (payload) => {
    if (!payload || payload.hasActiveGlobalDiscounts !== true) {
        return null;
    }

    if (!Array.isArray(payload.discounts) || payload.discounts.length === 0) {
        return null;
    }

    return payload;
};

export const discountsForCompany = (payload, companyRuc) => {
    const normalized = normalizeActiveGlobalDiscount(payload);
    if (!normalized) {
        return [];
    }

    if (!companyRuc) {
        return normalized.discounts;
    }

    return normalized.discounts.filter(
        (discount) => String(discount.companyRuc || '') === String(companyRuc)
    );
};

export const formatDiscountValue = (discount) => {
    const value = Number(discount?.value);
    if (!Number.isFinite(value)) {
        return null;
    }

    const type = String(discount?.type || '').toLowerCase();
    if (type === percentageType) {
        const rounded = Math.round(value * 100) / 100;
        return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(2)}%`;
    }

    if (flatTypes.has(type)) {
        return `$${value.toFixed(2)}`;
    }

    return null;
};

const uniqueLabels = (discounts) => [...new Set(discounts.map(formatDiscountValue).filter(Boolean))];

const formatCompanyList = (names) => {
    const uniqueNames = [...new Set(names.filter(Boolean))];
    if (uniqueNames.length === 0) {
        return '';
    }
    if (uniqueNames.length === 1) {
        return uniqueNames[0];
    }
    if (uniqueNames.length === 2) {
        return `${uniqueNames[0]} y ${uniqueNames[1]}`;
    }
    return `${uniqueNames.slice(0, -1).join(', ')} y ${uniqueNames[uniqueNames.length - 1]}`;
};

const bannerMessageForDiscount = (discount, companyText) => {
    const valueLabel = formatDiscountValue(discount);
    const place = companyText ? ` en ${companyText}` : '';
    const type = String(discount?.type || '').toLowerCase();
    const reason = discountReason(discount);
    const hasAmount = Boolean(valueLabel) && (type === percentageType || flatTypes.has(type));

    if (reason === 'welcome' && hasAmount) {
        return `${valueLabel} de descuento en tu primera compra${place}.`;
    }

    if (reason === 'welcome') {
        return `Descuento de bienvenida en tu primera compra${place}.`;
    }

    if (reason === 'holiday' && hasAmount) {
        return `${valueLabel} de descuento por feriado${place}.`;
    }

    if (reason === 'holiday') {
        return `Descuento por feriado${place}.`;
    }

    return `Hay un descuento para tu compra${place}.`;
};

export const buildDiscountBannerGroups = (discounts, resolveCompanyName = (companyRuc) => companyRuc) => {
    if (!Array.isArray(discounts) || discounts.length === 0) {
        return [];
    }

    const byKind = new Map();
    discounts.forEach((discount) => {
        const kind = discount?.kind || 'other';
        if (!byKind.has(kind)) {
            byKind.set(kind, []);
        }
        byKind.get(kind).push(discount);
    });

    return [...byKind.entries()].map(([kind, kindDiscounts]) => {
        const byOffer = new Map();
        kindDiscounts.forEach((discount) => {
            const offerKey = [
                String(discount?.type || '').toLowerCase(),
                Number(discount?.value),
                Number(discount?.minPurchaseAmount || 0),
            ].join('|');
            if (!byOffer.has(offerKey)) {
                byOffer.set(offerKey, []);
            }
            byOffer.get(offerKey).push(discount);
        });

        const lines = [...byOffer.values()].map((offerDiscounts) => {
            const companyText = formatCompanyList(
                offerDiscounts.map((discount) => resolveCompanyName(discount.companyRuc))
            );
            return {
                key: offerDiscounts.map((discount) => discount.companyRuc).join('-'),
                message: bannerMessageForDiscount(offerDiscounts[0], companyText),
                minimumLabel: formatMinimumPurchaseWithTax(offerDiscounts[0]?.minPurchaseAmount),
            };
        });

        return { kind, lines };
    });
};

export const buildBannerMessage = (payload, resolveCompanyName) => {
    const groups = buildDiscountBannerGroups(discountsForCompany(payload), resolveCompanyName);
    if (groups.length === 0) {
        return null;
    }
    return groups.flatMap((group) => group.lines.map((line) => line.message)).join(' ');
};

export const buildSummaryNote = (discounts, appliedCode) => {
    const normalizedAppliedCode = String(appliedCode || '').trim().toUpperCase();
    const alreadyApplied = Boolean(
        normalizedAppliedCode &&
        discounts.some((discount) => String(discount.code || '').trim().toUpperCase() === normalizedAppliedCode)
    );

    if (alreadyApplied) {
        return 'Este descuento es de un solo uso y ya está incluido en este pedido.';
    }

    return 'Este descuento es de un solo uso y se podrá aplicar a este pedido.';
};

export const summaryValueLabel = (discounts) => {
    const labels = uniqueLabels(discounts);
    return labels.length === 1 ? labels[0] : null;
};

export const readAppliedCoupon = (source) => {
    if (!source || typeof source !== 'object') {
        return null;
    }

    const candidates = [source.appliedCoupon, source.coupon, source.discount];
    return candidates.find((item) => (
        item
        && typeof item === 'object'
        && (item.code || item.reason || item.kind || item.type)
    )) || null;
};

export const resolveSummaryDiscount = ({
    activeGlobalDiscount = null,
    companyRuc = null,
    appliedCoupon = null,
} = {}) => {
    const coupon = readAppliedCoupon({ appliedCoupon }) || (
        appliedCoupon && typeof appliedCoupon === 'object' ? appliedCoupon : null
    );
    const code = String(coupon?.code || '').trim().toUpperCase();
    const companyDiscounts = discountsForCompany(activeGlobalDiscount, companyRuc);
    const matched = code
        ? companyDiscounts.find((discount) => String(discount?.code || '').trim().toUpperCase() === code)
        : null;

    if (!matched && !coupon) {
        return null;
    }

    return {
        ...(matched || {}),
        ...(coupon || {}),
        reason: coupon?.reason || coupon?.kind || matched?.reason || matched?.kind,
        type: coupon?.type || matched?.type,
        value: coupon?.value ?? matched?.value,
        code: coupon?.code || matched?.code,
    };
};

export const discountSummaryLabel = (discount) => {
    const hasReason = Boolean(discountReason(discount));
    const reason = hasReason ? discountReasonLabel(discount) : 'Descuento';
    const type = String(discount?.type || '').toLowerCase();
    const valueLabel = formatDiscountValue(discount);

    if (type === percentageType && valueLabel) {
        return `${reason} (${valueLabel})`;
    }

    return reason;
};

export const formatMinimumPurchaseWithTax = (amount) => {
    const base = Number(amount);
    if (!Number.isFinite(base) || base <= 0) {
        return null;
    }

    const taxRate = Number(config.tax.percentage);
    const rate = Number.isFinite(taxRate) ? taxRate : 0.15;
    const withTax = Math.round(base * (1 + rate) * 100) / 100;
    return `$${withTax.toFixed(2)}`;
};

export const GLOBAL_DISCOUNT_BELOW_MINIMUM_MESSAGE =
    'El descuento se quitó porque la compra ya no alcanza el monto mínimo.';

export const discountMeetsMinimumPurchase = (discount, itemsSubtotal) => {
    const subtotal = Number(itemsSubtotal);
    if (!discount || !Number.isFinite(subtotal)) {
        return false;
    }

    const minimum = Number(discount?.minPurchaseAmount || 0);
    return subtotal >= (Number.isFinite(minimum) ? minimum : 0);
};

export const findGlobalDiscountByCode = (discounts, code) => {
    const normalizedCode = String(code || '').trim().toUpperCase();
    if (!normalizedCode || !Array.isArray(discounts)) {
        return null;
    }

    return discounts.find((discount) => (
        String(discount?.code || '').trim().toUpperCase() === normalizedCode
    )) || null;
};

export const appliedGlobalDiscountBelowMinimum = ({
    discounts,
    appliedCoupon,
    itemsSubtotal,
} = {}) => {
    const discount = findGlobalDiscountByCode(discounts, appliedCoupon?.code);
    if (!discount) {
        return false;
    }

    return !discountMeetsMinimumPurchase(discount, itemsSubtotal);
};

export const getApplicableGlobalDiscount = (discounts, { appliedCode, itemsSubtotal } = {}) => {
    if (!Array.isArray(discounts) || discounts.length === 0) {
        return null;
    }

    if (String(appliedCode || '').trim()) {
        return null;
    }

    const subtotal = Number(itemsSubtotal);
    if (!Number.isFinite(subtotal)) {
        return null;
    }

    const applicable = discounts.filter((discount) => {
        const code = String(discount?.code || '').trim();
        if (!code) {
            return false;
        }

        return discountMeetsMinimumPurchase(discount, subtotal);
    });

    return applicable[0] || null;
};

export const sharedMinimumPurchaseLabel = (discounts) => {
    if (!Array.isArray(discounts) || discounts.length === 0) {
        return null;
    }

    const amounts = [...new Set(discounts.map((discount) => Number(discount?.minPurchaseAmount || 0)))];
    if (amounts.length !== 1) {
        return null;
    }

    return formatMinimumPurchaseWithTax(amounts[0]);
};
